import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  AcademicCapIcon,
  ArrowRightIcon,
  BriefcaseIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import { CourseItem, listCoursesApi } from "../../features/courses/api/course.api"
import { SelectVacanciesModal } from "../../features/courses/ui/SelectVacanciesModal"

/* ── helpers ─────────────────────────────────────────────── */

const withHttps = (url?: string) => {
  if (!url) return null
  if (url.startsWith("http")) return url
  return `https://${url}`
}

const courseWord = (n: number) => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 14) return "курсов"
  if (mod10 === 1) return "курс"
  if (mod10 >= 2 && mod10 <= 4) return "курса"
  return "курсов"
}

/* ── sub-components ──────────────────────────────────────── */

const PriceBadge = ({ type, amount }: { type: "free" | "paid" | "mixed"; amount: number }) => {
  if (type === "free")
    return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">Бесплатно</span>
  if (type === "mixed")
    return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-violet-100 text-violet-700 ring-1 ring-violet-200">От ${amount}</span>
  return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 ring-1 ring-blue-200">${amount}</span>
}

const CoursePlaceholder = ({ title }: { title: string }) => (
  <div className="w-full h-44 bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center rounded-t-xl flex-shrink-0">
    <div className="text-center px-4">
      <AcademicCapIcon className="w-10 h-10 text-white/60 mx-auto mb-2" />
      <p className="text-white/90 font-medium text-sm line-clamp-2">{title}</p>
    </div>
  </div>
)

const SkeletonCard = () => (
  <div className="rounded-xl border bg-white overflow-hidden animate-pulse">
    <div className="h-44 bg-gray-100" />
    <div className="p-4 space-y-3">
      <div className="h-4 bg-gray-100 rounded w-3/4" />
      <div className="h-3 bg-gray-100 rounded w-full" />
      <div className="h-3 bg-gray-100 rounded w-2/3" />
      <div className="flex justify-between pt-1">
        <div className="h-5 bg-gray-100 rounded w-16" />
        <div className="h-5 bg-gray-100 rounded w-20" />
      </div>
    </div>
  </div>
)

/* ── main ────────────────────────────────────────────────── */

export const MentorCoursesPage = () => {
  const navigate = useNavigate()

  const [courses, setCourses] = useState<CourseItem[]>([])
  const [loading, setLoading] = useState(false)

  const [limit] = useState(6)
  const [offset, setOffset] = useState(0)
  const [total, setTotal] = useState(0)

  const [vacanciesCourse, setVacanciesCourse] = useState<CourseItem | null>(null)

  const authorId = localStorage.getItem("user_id")

  useEffect(() => {
    if (!authorId) return
    setLoading(true)
    listCoursesApi(limit, offset, authorId)
      .then((res) => {
        setCourses(res.data.items)
        setTotal(res.data.total)
      })
      .finally(() => setLoading(false))
  }, [limit, offset, authorId])

  const totalPages = Math.ceil(total / limit)
  const currentPage = Math.floor(offset / limit) + 1

  return (
    <DashboardLayout title="Мои курсы">

      {/* Header bar */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-gray-900">Мои курсы</h2>
          <p className="text-sm text-gray-500 mt-0.5">Курсы, на которых вы сейчас являетесь ментором</p>
        </div>
        {total > 0 && (
          <span className="text-sm text-gray-400 bg-gray-50 border border-gray-100 rounded-lg px-3 py-1.5">
            {total} {courseWord(total)}
          </span>
        )}
      </div>

      {/* Skeleton */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      )}

      {/* Empty */}
      {!loading && courses.length === 0 && (
        <div className="bg-white rounded-xl border border-dashed border-gray-200 py-20 text-center">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <AcademicCapIcon className="w-7 h-7 text-blue-500" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Курсов пока нет</h3>
          <p className="text-sm text-gray-400">Вас пока не назначили ментором ни на один курс</p>
        </div>
      )}

      {/* Grid */}
      {!loading && courses.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {courses.map((c) => {
            const cover = withHttps(c.cover_image)
            return (
              <div
                key={c.id}
                className="group bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md hover:border-gray-200 transition-all cursor-pointer"
                onClick={() => navigate(`/courses/${c.id}`)}
              >
                {/* Cover */}
                {cover ? (
                  <div className="relative h-44 overflow-hidden flex-shrink-0">
                    <img src={cover} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>
                ) : (
                  <CoursePlaceholder title={c.title} />
                )}

                {/* Body */}
                <div className="p-4 space-y-2">
                  <h3 className="font-semibold text-gray-900 line-clamp-1">{c.title}</h3>

                  {c.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed">{c.description}</p>
                  )}

                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-gray-400 bg-gray-50 border border-gray-100 rounded px-2 py-0.5 uppercase tracking-wide font-medium">
                      {c.language}
                    </span>
                    <PriceBadge type={c.price.type} amount={c.price.amount} />
                  </div>
                </div>

                {/* CTA footer */}
                <div className="px-4 pb-4 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-sm text-blue-600 font-medium group-hover:gap-2.5 transition-all">
                    Смотреть курс
                    <ArrowRightIcon className="w-4 h-4" />
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setVacanciesCourse(c)
                    }}
                    className="flex items-center gap-1.5 text-sm text-gray-600 font-medium border border-gray-200 rounded-lg px-2.5 py-1.5 hover:border-blue-200 hover:text-blue-600 hover:bg-blue-50/40 transition-colors"
                  >
                    <BriefcaseIcon className="w-4 h-4" />
                    Вакансии
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="mt-6 flex items-center justify-between bg-white rounded-xl border border-gray-100 px-5 py-3">
          <span className="text-sm text-gray-500">
            Страница {currentPage} из {totalPages} · {total} {courseWord(total)}
          </span>
          <div className="flex gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              Пред.
            </button>
            <button
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition"
            >
              След.
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <SelectVacanciesModal
        open={!!vacanciesCourse}
        onClose={() => setVacanciesCourse(null)}
        courseId={vacanciesCourse?.id ?? ""}
        courseTitle={vacanciesCourse?.title}
        onForbidden={() => {
          if (vacanciesCourse) {
            setCourses((prev) => prev.filter((c) => c.id !== vacanciesCourse.id))
          }
        }}
      />

    </DashboardLayout>
  )
}
